<template>
  <div
    class="group relative z-10 flex h-full flex-col border-r border-divider bg-white shadow-main transition-all duration-300"
    :class="containerClass"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div
      v-show="isVisible"
      class="menu-column flex min-h-0 flex-1 flex-col overflow-y-hidden px-2 pt-5 pb-3"
    >
      <div v-if="searchable" class="shrink-0 px-2">
        <search-input
          v-model="searchQuery"
          :search-term="$t('COMMON.SETTINGS')"
          no-margin
          no-padding
        />
      </div>

      <div
        class="menu-wrapper flex min-h-0 flex-1 flex-col space-y-8 overflow-x-hidden overflow-y-hidden group-hover:overflow-y-auto"
        :class="{ 'mt-11': searchable }"
      >
        <div v-for="(section, index) in filteredOptions" :key="section?.name">
          <access-control :roles="section?.acl || allRoles">
            <div
              v-if="section?.name"
              class="mb-4 pl-3 text-xxs font-semibold uppercase tracking-[3px] text-label"
              :class="{
                'mt-7': index !== 0,
                'mt-3': !searchable && index === 0,
              }"
            >
              {{ section.name }}
            </div>
            <div
              v-for="(option, i) in section?.options"
              :key="i"
              class="not-first:my-1.5"
            >
              <access-control :roles="option.acl || allRoles">
                <template
                  v-if="!option.confirm && !option.tabs && !option.tabComponent"
                >
                  <router-link v-slot="{ isActive }" :to="option.link || ''">
                    <sidebar-submenu-item :option="option" :active="isActive" />
                  </router-link>
                </template>
                <template
                  v-else-if="
                    !option.confirm && (option.tabs || option.tabComponent)
                  "
                >
                  <lf-details
                    :model-value="isTabOpen(option.value ?? option.label)"
                    class="justify-between"
                    @update:model-value="
                      (val: boolean) =>
                        setTabOpen(option.value ?? option.label, val)
                    "
                  >
                    <template #summary="{ isOpen, setOpen }">
                      <router-link
                        :to="option?.link || ''"
                        class="flex w-full"
                        @click="emit('tab-click', option.value)"
                      >
                        <sidebar-submenu-item :option="option" />
                      </router-link>
                      <div
                        class="flex w-10 cursor-pointer items-center justify-center rounded-sm px-2 py-4 hover:bg-blue-100"
                        @click="() => setOpen(!isOpen)"
                      >
                        <icon-chevron-up
                          class="transform transition-all"
                          :class="{ 'rotate-180': isOpen }"
                          fill="#868D9C"
                        />
                      </div>
                    </template>
                    <template v-if="option.tabs">
                      <sidebar-submenu-tab
                        v-for="tab in option.tabs"
                        :key="tab"
                        :option="option"
                        :tab="tab"
                        :active-tab="activeTab"
                        @click="emit('update:activeTab', tab)"
                      />
                      <router-link
                        v-for="tabLink in option.additionalTabLinks"
                        :key="tabLink.link"
                        :to="tabLink?.link || ''"
                        class="flex w-full"
                      >
                        <side-menu-item
                          class="m-0 pl-7 leading-4"
                          :active="activeTab === tabLink.label"
                          is-slim
                        >
                          <span class="text-xs">
                            {{ tabLink.label }}
                          </span>
                        </side-menu-item>
                      </router-link>
                    </template>
                    <component
                      :is="option.tabComponent"
                      v-else-if="option.tabComponent"
                      v-bind="option.tabComponentProps"
                    />
                  </lf-details>
                </template>
                <template v-else>
                  <confirm-modal
                    v-if="showConfirmModal"
                    :title="option.confirm?.title ?? option.label"
                    :description="option.confirm?.description ?? ''"
                    :confirm-button="option.confirm?.btnText ?? 'Submit'"
                    :confirm-button-type="option.confirm?.btnType ?? 'success'"
                    :close="() => (showConfirmModal = false)"
                    @confirm="executeHandler(option)"
                  />
                  <sidebar-submenu-item
                    class="my-1-5"
                    :option="option"
                    :active="isOptionActive(option)"
                    @click="showConfirmModal = true"
                  />
                </template>
              </access-control>
            </div>
          </access-control>
        </div>
      </div>
      <div class="shrink-0">
        <slot name="bottom-option" />
      </div>
    </div>
    <button
      v-tooltip="{
        content: isLocked
          ? $t('SIDEBAR.HIDE_SIDEBAR')
          : $t('SIDEBAR.LOCK_SIDEBAR_OPEN'),
        placement: 'right',
      }"
      class="absolute top-5 z-50 flex h-6 w-6 items-center justify-center rounded-4 border border-divider bg-white transition-opacity duration-200"
      :class="toggleButtonClass"
      data-cy="sidebar-lock-button"
      @click="toggle"
    >
      <icon-chevron-right
        class="h-3 w-3 text-label"
        :class="{ 'rotate-180': isLocked }"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import {
  CLIENT_ROLE_GROUP,
  FUNDER_ROLE_GROUP,
  LENDFLOW_ROLE_GROUP,
} from "@/helpers/constants";
import { useLocalStorageSetting } from "@/hooks/options";

import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import SidebarSubmenuItem from "@/components/sidebar/SidebarSubmenuItem.vue";
import SidebarSubmenuTab from "@/components/sidebar/SidebarSubmenuTab.vue";
import LfDetails from "@/components/ui/LfDetails.vue";
import SideMenuItem from "@/components/ui/SideMenuItem.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

import type {
  ISidebarSubmenuOption,
  ISidebarSubmenuSection,
} from "@/models/common";

const allRoles = [
  ...LENDFLOW_ROLE_GROUP,
  ...CLIENT_ROLE_GROUP,
  ...FUNDER_ROLE_GROUP,
];
const { path, matched } = useRoute();

const {
  options = [],
  hasTabs = false,
  activeTab = "",
  openTabValue = "",
  searchable = false,
} = defineProps<{
  options?: (ISidebarSubmenuSection | null)[];
  hasTabs?: boolean;
  activeTab?: string;
  openTabValue?: string;
  searchable?: boolean;
}>();

const emit = defineEmits(["sidebarToggled", "update:activeTab", "tab-click"]);

const isLocked = useLocalStorageSetting("sidebarSubmenuLocked", true);
const isHovering = ref(false);
const isVisible = computed(() => isLocked.value || isHovering.value);

const toggle = () => {
  isLocked.value = !isLocked.value;
  if (isLocked.value) {
    isHovering.value = false;
  }
};

const onMouseEnter = () => {
  if (!isLocked.value) {
    isHovering.value = true;
  }
};

const onMouseLeave = () => {
  if (!isLocked.value) {
    isHovering.value = false;
  }
};

const tabOpenStates = ref<Record<string, boolean>>({});
const showConfirmModal = ref(false);
const searchQuery = ref("");

const matchesSearch = (option: ISidebarSubmenuOption, query: string) => {
  if (option.label.toLowerCase().includes(query)) {
    return true;
  }

  if (option.tabs?.some((tab) => tab.toLowerCase().includes(query))) {
    return true;
  }

  if (
    option.additionalTabLinks?.some((tabLink) =>
      tabLink.label.toLowerCase().includes(query)
    )
  ) {
    return true;
  }

  return false;
};

const filteredOptions = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();

  if (!query) {
    return options;
  }

  return options
    .map((section) => {
      if (!section) {
        return null;
      }

      const filteredSectionOptions = section.options.filter((option) =>
        matchesSearch(option, query)
      );

      if (!filteredSectionOptions.length) {
        return null;
      }

      return {
        ...section,
        options: filteredSectionOptions,
      };
    })
    .filter(Boolean);
});

const containerClass = computed(() => {
  if (isVisible.value) {
    const classes = ["w-fit", "max-w-[25vw]"];
    if (hasTabs) {
      classes.push("min-w-50");
    }
    return classes;
  }
  return ["w-6"];
});

const toggleButtonClass = computed(() => {
  const base = "right-0 translate-x-1/2";
  if (isLocked.value) {
    return [base, "opacity-0 group-hover:opacity-100"];
  }
  return [base, "opacity-100"];
});

const isTabOpen = (key: string) => {
  if (key in tabOpenStates.value) {
    return tabOpenStates.value[key];
  }

  if (!openTabValue) {
    return true;
  }

  return openTabValue === key;
};

const setTabOpen = (key: string, value: boolean) => {
  tabOpenStates.value[key] = value;
};

const isOptionActive = (option: ISidebarSubmenuOption) => {
  if (!option.link) {
    return false;
  }

  if (typeof option.link === "object") {
    const { name } = option.link;
    return matched.some((match) => match.name === name);
  }

  return path.includes(option.link);
};

const executeHandler = (option: ISidebarSubmenuOption) => {
  if (option.confirm?.handler) {
    option.confirm.handler();
  }
};

watch(isVisible, (val) => {
  emit("sidebarToggled", val);
});

watch(
  () => openTabValue,
  () => {
    tabOpenStates.value = {};
  }
);

onMounted(() => {
  emit("sidebarToggled", isVisible.value);
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.menu-column {
  min-height: 0;
  flex: 1 1 0%;
}

.menu-wrapper {
  min-height: 0;
  flex: 1 1 0%;
}
</style>
